import React, { useCallback, useEffect, useMemo, useRef } from "react"
import PropTypes from "prop-types"
import { Box, Skeleton } from "@mui/material"
import "@syncfusion/ej2/material.css"
import {
  ChartComponent,
  SeriesCollectionDirective,
  SeriesDirective,
  Inject,
  Category,
  DataLabel,
  BarSeries,
} from "@syncfusion/ej2-react-charts"
import OverviewCard, { OVERVIEW_CHART_CARD_MIN_HEIGHT_PX } from "./OverviewCard"
import { OVERVIEW_CHART_FOOTER_MIN_HEIGHT_PX } from "./constants/overviewGrid"
import { formatChartNumber } from "./utils/numberFormat"

const BarChartCard = ({
  dataSource,
  title,
  infoText,
  isLoading,
  currency,
  reserveFooterSlot = false,
  fill = "#B5C0FD",
  pointColorMapping,
  categoryInversed = false,
}) => {
  const containerRef = useRef(null)
  const chartRef = useRef(null)

  const chartData = useMemo(
    () =>
      (dataSource ?? []).map(point => ({
        ...point,
        label: `${formatChartNumber(point.y)}${currency ? ` ${currency}` : ""}`,
      })),
    [dataSource, currency],
  )

  const chartMarginRight = useMemo(() => {
    const longestLabel = chartData.reduce(
      (max, point) => Math.max(max, point.label.length),
      0,
    )

    return Math.min(120, Math.max(32, longestLabel * 7))
  }, [chartData])

  const requestChartRefresh = useCallback(() => {
    chartRef.current?.refresh?.()
  }, [])

  useEffect(() => {
    if (isLoading) return
    const ro = new ResizeObserver(() => {
      requestAnimationFrame(requestChartRefresh)
    })
    const el = containerRef.current
    if (el) ro.observe(el)
    return () => ro.disconnect()
  }, [isLoading, requestChartRefresh, chartData])

  const handleAxisLabelRender = args => {
    const axis = args?.axis
    // El eje de categorías (p. ej. Mano de obra, Materiales) no debe formatearse como número.
    if (!axis || axis.valueType === "Category") return
    args.text = formatChartNumber(args.value, 0)
  }

  return (
    <OverviewCard
      title={title}
      infoText={infoText}
      loading={isLoading}
      sx={{ minHeight: OVERVIEW_CHART_CARD_MIN_HEIGHT_PX }}
    >
      <Box
        ref={containerRef}
        sx={{
          width: "100%",
          maxWidth: "100%",
          minWidth: 0,
          flexShrink: 0,
        }}
      >
        {isLoading ? (
          <Skeleton variant="rectangular" width="100%" height={300} />
        ) : (
          <ChartComponent
            ref={chartRef}
            width="100%"
            height="300px"
            background="white"
            enableResize={true}
            margin={{ top: 8, right: chartMarginRight, bottom: 8, left: 8 }}
            primaryXAxis={{
              valueType: "Category",
              ...(categoryInversed ? { isInversed: true } : {}),
            }}
            axisLabelRender={handleAxisLabelRender}
            tooltip={{ enable: false }}
          >
            <Inject services={[BarSeries, DataLabel, Category]} />
            <SeriesCollectionDirective>
              <SeriesDirective
                dataSource={chartData}
                xName="x"
                yName="y"
                type="Bar"
                fill={pointColorMapping ? undefined : fill}
                pointColorMapping={pointColorMapping}
                marker={{
                  dataLabel: {
                    visible: true,
                    name: "label",
                    position: "Outer",
                    alignment: "Middle",
                    labelIntersectAction: "None",

                    font: { size: 12, color: "#414141", fontWeight: "bold" },
                  },
                }}
              />
            </SeriesCollectionDirective>
          </ChartComponent>
        )}
      </Box>
      {reserveFooterSlot ? (
        <Box
          sx={{
            flexShrink: 0,
            minHeight: OVERVIEW_CHART_FOOTER_MIN_HEIGHT_PX,
          }}
          aria-hidden
        />
      ) : null}
    </OverviewCard>
  )
}

BarChartCard.propTypes = {
  dataSource: PropTypes.arrayOf(
    PropTypes.shape({
      x: PropTypes.string.isRequired,
      y: PropTypes.number.isRequired,
    }),
  ).isRequired,
  title: PropTypes.string.isRequired,
  infoText: PropTypes.string,
  isLoading: PropTypes.bool,
  currency: PropTypes.string,
  /** Alineación con donut en Costes (dos columnas). */
  reserveFooterSlot: PropTypes.bool,
  fill: PropTypes.string,
  pointColorMapping: PropTypes.string,
  categoryInversed: PropTypes.bool,
}

export default BarChartCard
